"use client";

import type React from "react";

import { useEffect, useRef, useState } from "react";
import { Button } from "../UI/Button";
import { RadioGroup } from "../UI/RadioGroup";
import { Trash2 } from "lucide-react";
import MuiPhoneNumber from "mui-phone-number";
import GenericApi from "@/api/genericApi";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse, ApiError } from "@/types/Api";
import { ValidationErrors } from "@/types/ValidationError";
import CustomLogger from "@/utils/CustomLogger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackBarAlert } from "@/hooks/useSnackbar";
import { BusinessInfoNEW, StatusForms } from "@/types/BusinessInfoNEW";
import {
  BusinessInfoQueryKey,
  CoPQueryKey,
  GenericStatusQueryKey,
} from "@/config/constants/QueryKeys";
import { getUser, getUserToken } from "@/utils/localStorage";
import { RootState, useAppDispatch } from "@/store";
import { useSelector } from "react-redux";
import {
  checkPermissionByKeyAndValue,
  isBitramp,
  validatePdfFile,
} from "@/utils/utils";
import { BusinessInfoDocType } from "@/types/BusinessDoc";
import axios from "axios";
import { setActiveTab } from "@/store/slices/componentSlice";
import { COUNTRIES } from "@/data/data";
import { Tooltip } from "flowbite-react";
import { AnimatePresence, motion } from "framer-motion";
import { Status } from "@/types/Status.tsx";

const BusinessInfoFormNEW: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const filteredCountries = COUNTRIES.filter((country) =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const [ownershipDropdownOpen, setOwnershipDropdownOpen] = useState<
    Record<number, boolean>
  >({});

  const [directorDropdownOpen, setDirectorDropdownOpen] = useState<
    Record<number, boolean>
  >({});

  const [countryDropdownOpen1, setCountryDropdownOpen1] = useState(false);
  const [
    highlightedIndexCountryDropdown1,
    setHighlightedIndexCountryDropdown1,
  ] = useState(-1);
  const listRefCountryDropdown1 = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setCountryDropdownOpen1(false);
        setHighlightedIndexCountryDropdown1(-1);
        setSearchTerm("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (
      highlightedIndexCountryDropdown1 >= 0 &&
      countryDropdownOpen1 &&
      listRefCountryDropdown1.current
    ) {
      const offset = 1;
      const el = listRefCountryDropdown1.current.children[
        highlightedIndexCountryDropdown1 + offset
      ] as HTMLDivElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndexCountryDropdown1, countryDropdownOpen1]);

  const [searchTermOwnership, setSearchTermOwnership] = useState<
    Record<number, string>
  >({});
  const [highlightedIndexOwnership, setHighlightedIndexOwnership] = useState<
    Record<number, number>
  >({});
  const listRefOwnership = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    Object.entries(highlightedIndexOwnership).forEach(([i, idx]) => {
      const index = Number(i);
      if (
        idx >= 0 &&
        ownershipDropdownOpen[index] &&
        listRefOwnership.current[index]
      ) {
        const offset = 1;
        const children = listRefOwnership.current[index]?.children;
        if (children && children[idx + offset]) {
          (children[idx + offset] as HTMLDivElement).scrollIntoView({
            block: "nearest",
          });
        }
      }
    });
  }, [highlightedIndexOwnership, ownershipDropdownOpen]);

  const ownershipDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        ownershipDropdownRef.current &&
        !ownershipDropdownRef.current.contains(event.target as Node)
      ) {
        // închide dropdown-ul
        setOwnershipDropdownOpen({});
        setHighlightedIndexOwnership({});
        setSearchTermOwnership({});
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [searchTermDirector, setSearchTermDirector] = useState<
    Record<number, string>
  >({});
  const [highlightedIndexDirector, setHighlightedIndexDirector] = useState<
    Record<number, number>
  >({});
  const listRefDirector = useRef<Record<number, HTMLDivElement | null>>({});
  useEffect(() => {
    Object.entries(highlightedIndexDirector).forEach(([i, idx]) => {
      const index = Number(i);
      if (
        idx >= 0 &&
        directorDropdownOpen[index] &&
        listRefDirector.current[index]
      ) {
        const offset = 1;
        const children = listRefDirector.current[index]?.children;
        if (children && children[idx + offset]) {
          (children[idx + offset] as HTMLDivElement).scrollIntoView({
            block: "nearest",
          });
        }
      }
    });
  }, [highlightedIndexDirector, directorDropdownOpen]);

  const directorDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        directorDropdownRef.current &&
        !directorDropdownRef.current.contains(event.target as Node)
      ) {
        // închide dropdown-ul
        setDirectorDropdownOpen({});
        setHighlightedIndexDirector({});
        setSearchTermDirector({});
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (idx: number) => {
    setOwnershipDropdownOpen((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleSelect = (idx: number, value: string) => {
    handleOwnershipChange(idx, "country", value);
    setOwnershipDropdownOpen((prev) => ({
      ...prev,
      [idx]: false,
    }));
  };

  const toggleDirectorDropdown = (idx: number) => {
    setDirectorDropdownOpen((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const handleDirectorSelect = (idx: number, value: string) => {
    handleDirectorChange(idx, "country", value);
    setDirectorDropdownOpen((prev) => ({
      ...prev,
      [idx]: false,
    }));
  };

  const baseUrl: string = GenericApi.baseUrl;
  const { showSnackBar } = useSnackBarAlert();
  const queryClient = useQueryClient();
  const user = getUser();
  const { selectedClient } = useSelector((state: RootState) => state.client);
  const dispatch = useAppDispatch();
  const today = new Date().toISOString().split("T")[0];

  const calculateMaximumDateToExp = () => {
    const today = new Date();
    const formatted = today.toISOString().split("T")[0];
    return formatted;
  };

  const calculateMaximum = calculateMaximumDateToExp();

  const getFileName = (filePath: File | string | null) => {
    if (!filePath) return "";
    if (filePath instanceof File) {
      return filePath.name;
    }
    return filePath.split("/").pop();
  };

  const handleOpenDocument = (link: string) => {
    window.open(link, "_blank");
  };

  const [clicked, _setClicked] = useState(() => {
    if (!selectedClient?.id) return false;
    return localStorage.getItem(`approved_${selectedClient.id}`) === "true";
  });

  useEffect(() => {
    if (clicked && selectedClient?.id) {
      localStorage.setItem(`approved_${selectedClient.id}`, "true");
    }
  }, [clicked, selectedClient?.id]);

  const [status, _setStatus] = useState<"approved" | "declined" | null>(() => {
    if (!selectedClient?.id) return null;
    return (
      (localStorage.getItem(`status_${selectedClient.id}`) as
        | "approved"
        | "declined") || null
    );
  });

  // Salvare în localStorage când se schimbă statusul
  useEffect(() => {
    if (selectedClient?.id) {
      if (status) {
        localStorage.setItem(`status_${selectedClient.id}`, status);
      } else {
        localStorage.removeItem(`status_${selectedClient.id}`);
      }
    }
  }, [status, selectedClient?.id]);

  const canEditBusinessInfo =
    checkPermissionByKeyAndValue(
      user?.permissions || [],
      "change",
      "businessinfo",
    ) || user?.is_superuser;

  const isDisabled = !(canEditBusinessInfo || !user?.is_staff);

  const canEdit = user?.is_superuser;

  const { data: userBusinessInfo } = useQuery<ApiResponse<BusinessInfoNEW>>({
    queryFn: async () =>
      GenericApi.get<BusinessInfoNEW>(
        `${
          selectedClient
            ? `${ApiRoutes.BUSINESSINFO}?user_id=${selectedClient.id}`
            : `${ApiRoutes.BUSINESSINFO}`
        }`,
      ),
    queryKey: [BusinessInfoQueryKey],
  });

  const localUser = getUser();

  const [incorporation_certificate, setIncorporationCertificate] =
    useState<File | null>(null);
  const [good_standing_certificate, setGoodStandingCertificate] =
    useState<File | null>(null);
  const [shareholders_certificate, setShareholdersCertificate] =
    useState<File | null>(null);
  const [directors_certificate, setDirectorsCertificate] =
    useState<File | null>(null);
  const [notice_of_registered_office, setNoticeOfRegisteredOffice] =
    useState<File | null>(null);
  const [id_of_person_managing_company, setIdOfPersonManagingCompany] =
    useState<File | null>(null);
  const [
    resolution_who_is_authorised_on_behalf,
    setResolutionWhoIsAuthorisedOnBehalf,
  ] = useState<File | null>(null);
  const [id_of_who_is_authorised_on_behalf, setIdOfWhoIsAuthorisedOnBehalf] =
    useState<File | null>(null);

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileName: string,
  ) => {
    const isValidated = validatePdfFile(
      e.target.files ? e.target.files[0] : null,
    );
    if (!isValidated) {
      return;
    }
    // setPOA(e.target.files ? e.target.files[0] : null);
    if (fileName === "certificate_of_incorporation") {
      setIncorporationCertificate(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "certificate_of_good_standing") {
      setGoodStandingCertificate(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "certificate_of_shareholders") {
      setShareholdersCertificate(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "certificate_of_directors") {
      setDirectorsCertificate(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "notice_of_registered_office") {
      setNoticeOfRegisteredOffice(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "identification_documents") {
      setIdOfPersonManagingCompany(e.target.files ? e.target.files[0] : null);
    }
    if (fileName === "resolution_specifying") {
      setResolutionWhoIsAuthorisedOnBehalf(
        e.target.files ? e.target.files[0] : null,
      );
    }
    if (fileName === "identification_documents_authorised") {
      setIdOfWhoIsAuthorisedOnBehalf(e.target.files ? e.target.files[0] : null);
    }
  };

  const addBusinessInfoFiles = useMutation<
    ApiResponse<BusinessInfoDocType>,
    ApiError<ValidationErrors>,
    {
      incorporation_certificate: File | null;
      good_standing_certificate: File | null;
      shareholders_certificate: File | null;
      directors_certificate: File | null;
      notice_of_registered_office: File | null;
      id_of_person_managing_company: File | null;
      resolution_who_is_authorised_on_behalf: File | null;
      id_of_who_is_authorised_on_behalf: File | null;
    }
  >({
    mutationFn: async ({
      incorporation_certificate,
      good_standing_certificate,
      shareholders_certificate,
      directors_certificate,
      notice_of_registered_office,
      id_of_person_managing_company,
      resolution_who_is_authorised_on_behalf,
      id_of_who_is_authorised_on_behalf,
    }): Promise<ApiResponse<BusinessInfoDocType>> => {
      const formData = new FormData();

      //TODO Fix this
      formData.append("incorporation_certificate", incorporation_certificate!);
      formData.append("good_standing_certificate", good_standing_certificate!);
      formData.append("shareholders_certificate", shareholders_certificate!);
      formData.append("directors_certificate", directors_certificate!);
      formData.append(
        "notice_of_registered_office",
        notice_of_registered_office!,
      );
      formData.append(
        "id_of_person_managing_company",
        id_of_person_managing_company!,
      );
      formData.append(
        "resolution_who_is_authorised_on_behalf",
        resolution_who_is_authorised_on_behalf!,
      );
      formData.append(
        "id_of_who_is_authorised_on_behalf",
        id_of_who_is_authorised_on_behalf!,
      );

      const apiResponse: ApiResponse<BusinessInfoDocType> = {
        data: null,
        error: null,
      };

      const url = `${
        selectedClient
          ? `${ApiRoutes.BUSINESSINFOFILES}?user_id=${selectedClient.id}`
          : ApiRoutes.BUSINESSINFOFILES
      }`;

      const res = await axios.post<ApiResponse<BusinessInfoDocType>>(
        `${baseUrl}${url}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getUserToken()}`,
          },
        },
      );

      if (res.status !== 200) {
        apiResponse.error = {
          name: "Error",
          message: "An error occurred",
          stack: "",
        } as Error;
        return apiResponse;
      }

      apiResponse.data = {
        incorporation_certificate:
          res?.data?.data?.incorporation_certificate ?? null,
        good_standing_certificate:
          res?.data?.data?.good_standing_certificate ?? null,
        shareholders_certificate:
          res?.data?.data?.shareholders_certificate ?? null,
        directors_certificate: res?.data?.data?.directors_certificate ?? null,
        notice_of_registered_office:
          res?.data?.data?.notice_of_registered_office ?? null,
        id_of_person_managing_company:
          res?.data?.data?.id_of_person_managing_company ?? null,
        resolution_who_is_authorised_on_behalf:
          res?.data?.data?.resolution_who_is_authorised_on_behalf ?? null,
        id_of_who_is_authorised_on_behalf:
          res?.data?.data?.id_of_who_is_authorised_on_behalf ?? null,
      };
      return apiResponse;
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({ queryKey: [BusinessInfoQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
    },
  });

  const [declineReason, setDeclineReason] = useState<string>(
    userBusinessInfo?.data?.status?.decline_reason ?? "",
  );

  const [formData, setFormData] = useState<BusinessInfoNEW>({
    company_legal_name: userBusinessInfo?.data?.company_legal_name ?? "",
    trade_name: userBusinessInfo?.data?.trade_name ?? "",
    registered_company_street_address:
      userBusinessInfo?.data?.registered_company_street_address ?? "",
    registered_company_street_address2:
      userBusinessInfo?.data?.registered_company_street_address2 ?? "",
    registered_company_post_code:
      userBusinessInfo?.data?.registered_company_post_code ?? "",
    registered_company_city:
      userBusinessInfo?.data?.registered_company_city ?? "",
    registered_company_country:
      userBusinessInfo?.data?.registered_company_country ?? "Gibraltar",
    registered_company_registration_number:
      userBusinessInfo?.data?.registered_company_registration_number ?? "",
    registered_company_incorporation_date:
      userBusinessInfo?.data?.registered_company_incorporation_date ?? "",
    registered_company_incorporation_city_and_country:
      userBusinessInfo?.data
        ?.registered_company_incorporation_city_and_country ?? "",
    registered_company_tin:
      userBusinessInfo?.data?.registered_company_tin ?? "",
    registered_company_registration_authority:
      userBusinessInfo?.data?.registered_company_registration_authority ?? "",
    registered_company_listed_on_stock_exchange:
      userBusinessInfo?.data?.registered_company_listed_on_stock_exchange ??
      "NO",
    business_overview_type_of_business:
      userBusinessInfo?.data?.business_overview_type_of_business ?? "",
    business_overview_contact_person:
      userBusinessInfo?.data?.business_overview_contact_person ?? "",
    business_overview_actual_company_address:
      userBusinessInfo?.data?.business_overview_actual_company_address ?? "",
    business_overview_phone:
      userBusinessInfo?.data?.business_overview_phone ?? "",
    business_overview_email:
      userBusinessInfo?.data?.business_overview_email ?? "",
    ownership_structures: [
      {
        entity_name: "",
        phone: "",
        email: "",
        nature_of_control: "",
        contact_person: "",
        pep: false,
        first_name: "",
        last_name: "",
        id_number: "",
        id_expiration: null,
        street_address: "",
        street_address2: "",
        post_code: "",
        city: "",
        country: "Gibraltar",
      },
    ],
    directors: [
      {
        phone: "",
        email: "",
        pep: false,
        first_name: "",
        last_name: "",
        id_number: "",
        id_expiration: "",
        street_address: "",
        street_address2: "",
        post_code: "",
        city: "",
        country: "Gibraltar",
      },
    ],

    business_model: userBusinessInfo?.data?.business_model ?? "",
    website: userBusinessInfo?.data?.website ?? "",
    expected_monthly_transaction_volume:
      userBusinessInfo?.data?.expected_monthly_transaction_volume ?? 0,
    expected_currency: userBusinessInfo?.data?.expected_currency ?? "EUR",

    legalQuestions: {
      prosecutionNotice: "NO",
      criminalConvictions: "NO",
      ongoingInvestigations: "NO",
      companyConductGreyList: "NO",
      companyConduct: "NO",
    },
    additional_comments: userBusinessInfo?.data?.additional_comments ?? "",
    documents: {
      incorporationCertificate: null as File | null,
      goodStandingCertificate: null as File | null,
      shareholdersCertificate: null as File | null,
      directorsCertificate: null as File | null,
      registeredOfficeNotice: null as File | null,
      managementIdentification: null as File | null,
      authorizationResolution: null as File | null,
      authorizedPersonIdentification: null as File | null,
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessActivitiesChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLegalQuestionChange = (question: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      legalQuestions: {
        ...prev.legalQuestions,
        [question]: value,
      },
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      registered_company_listed_on_stock_exchange: value,
    }));
  };

  const handleOwnershipChange = (
    index: number,
    field: string,
    value: boolean | string,
  ) => {
    setFormData((prev) => {
      const updatedOwnershipStructures = [...prev.ownership_structures];
      updatedOwnershipStructures[index] = {
        ...updatedOwnershipStructures[index],
        [field]: value,
      };

      console.log(
        `Ownership structure at index ${index} updated:`,
        updatedOwnershipStructures[index],
      );

      return {
        ...prev,
        ownership_structures: updatedOwnershipStructures,
      };
    });
  };

  const handleDirectorChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    setFormData((prev) => {
      const updatedDirectors = [...prev.directors];
      updatedDirectors[index] = { ...updatedDirectors[index], [field]: value };
      return { ...prev, directors: updatedDirectors };
    });
  };

  useEffect(() => {
    if (userBusinessInfo?.data?.ownership_structures) {
      setFormData((prev) => ({
        ...prev,
        ownership_structures: userBusinessInfo.data?.ownership_structures ?? [],
      }));
    }
    if (userBusinessInfo?.data?.directors) {
      setFormData((prev) => ({
        ...prev,
        directors: userBusinessInfo.data?.directors ?? [],
      }));
    }
    if (userBusinessInfo?.data) {
      const normalizedStockExchangeStatus =
        userBusinessInfo.data?.registered_company_listed_on_stock_exchange?.toLowerCase() ===
        "yes"
          ? "Yes"
          : "No";

      setFormData((prev) => ({
        ...prev,
        company_legal_name: userBusinessInfo.data?.company_legal_name ?? "",
        trade_name: userBusinessInfo.data?.trade_name ?? "",
        registered_company_street_address:
          userBusinessInfo.data?.registered_company_street_address ?? "",
        registered_company_street_address2:
          userBusinessInfo.data?.registered_company_street_address2 ?? "",
        registered_company_post_code:
          userBusinessInfo.data?.registered_company_post_code ?? "",
        registered_company_city:
          userBusinessInfo.data?.registered_company_city ?? "",
        registered_company_country:
          userBusinessInfo.data?.registered_company_country ?? "Gibraltar",
        registered_company_registration_number:
          userBusinessInfo.data?.registered_company_registration_number ?? "",
        registered_company_incorporation_date:
          userBusinessInfo.data?.registered_company_incorporation_date ?? "",
        registered_company_incorporation_city_and_country:
          userBusinessInfo.data
            ?.registered_company_incorporation_city_and_country ?? "",
        registered_company_tin:
          userBusinessInfo.data?.registered_company_tin ?? "",
        registered_company_registration_authority:
          userBusinessInfo.data?.registered_company_registration_authority ??
          "",
        registered_company_listed_on_stock_exchange:
          normalizedStockExchangeStatus,
        business_overview_type_of_business:
          userBusinessInfo.data?.business_overview_type_of_business ?? "",
        business_overview_contact_person:
          userBusinessInfo.data?.business_overview_contact_person ?? "",
        business_overview_actual_company_address:
          userBusinessInfo.data?.business_overview_actual_company_address ?? "",
        business_overview_phone:
          userBusinessInfo.data?.business_overview_phone ?? "",
        business_overview_email:
          userBusinessInfo.data?.business_overview_email ?? "",
        business_model: userBusinessInfo.data?.business_model ?? "",
        website: userBusinessInfo.data?.website ?? "",
        expected_monthly_transaction_volume:
          userBusinessInfo.data?.expected_monthly_transaction_volume ?? 0,
        expected_currency: userBusinessInfo.data?.expected_currency ?? "EUR",
        additional_comments: userBusinessInfo.data?.additional_comments ?? "",
        decline_reason: userBusinessInfo.data?.status?.decline_reason ?? "",
      }));
      setDeclineReason(userBusinessInfo.data?.status?.decline_reason ?? "");
    }
  }, [userBusinessInfo, selectedClient]);

  const addOwnershipStructure = () => {
    setFormData((prev) => ({
      ...prev,
      ownership_structures: [
        ...prev.ownership_structures,
        {
          entity_name: "",
          phone: "",
          email: "",
          nature_of_control: "",
          contact_person: "",
          pep: false,
          first_name: "",
          last_name: "",
          id_number: "",
          id_expiration: null,
          street_address: "",
          street_address2: "",
          post_code: "",
          city: "",
          country: "Gibraltar",
        },
      ],
    }));
  };

  const removeOwnershipStructure = (index: number) => {
    if (formData.ownership_structures.length <= 1) return; // Keep at least one

    setFormData((prev) => ({
      ...prev,
      ownership_structures: prev.ownership_structures.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const addDirector = () => {
    setFormData((prev) => ({
      ...prev,
      directors: [
        ...prev.directors,
        {
          phone: "",
          email: "",
          pep: false,
          first_name: "",
          last_name: "",
          id_number: "",
          id_expiration: "",
          street_address: "",
          street_address2: "",
          post_code: "",
          city: "",
          country: "Gibraltar",
        },
      ],
    }));
  };

  const removeDirector = (index: number) => {
    if (formData.directors.length <= 1) return; // Keep at least one

    setFormData((prev) => ({
      ...prev,
      directors: prev.directors.filter((_, i) => i !== index),
    }));
  };

  const addBusinessInfo = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    BusinessInfoNEW
  >({
    mutationFn: (data) => {
      return GenericApi.put<BusinessInfoNEW>(`${ApiRoutes.BUSINESSINFO}`, {
        ...data,
      });
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [BusinessInfoQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      dispatch(setActiveTab(1));
      showSnackBar(
        "Business Information Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
      addBusinessInfoFiles.mutate({
        incorporation_certificate: incorporation_certificate,
        good_standing_certificate: good_standing_certificate,
        shareholders_certificate: shareholders_certificate,
        directors_certificate: directors_certificate,
        notice_of_registered_office: notice_of_registered_office,
        id_of_person_managing_company: id_of_person_managing_company,
        resolution_who_is_authorised_on_behalf:
          resolution_who_is_authorised_on_behalf,
        id_of_who_is_authorised_on_behalf: id_of_who_is_authorised_on_behalf,
      });
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const handleEditBussinesInfo = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    BusinessInfoNEW
  >({
    mutationFn: (data) => {
      return GenericApi.put<BusinessInfoNEW>(
        `${ApiRoutes.BUSINESSINFO}?user_id=${
          selectedClient ? selectedClient.id : ""
        }`,
        {
          ...data,
        },
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({
        queryKey: [BusinessInfoQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      addBusinessInfoFiles.mutate({
        incorporation_certificate: incorporation_certificate,
        good_standing_certificate: good_standing_certificate,
        shareholders_certificate: shareholders_certificate,
        directors_certificate: directors_certificate,
        notice_of_registered_office: notice_of_registered_office,
        id_of_person_managing_company: id_of_person_managing_company,
        resolution_who_is_authorised_on_behalf:
          resolution_who_is_authorised_on_behalf,
        id_of_who_is_authorised_on_behalf: id_of_who_is_authorised_on_behalf,
      });
      showSnackBar(
        "Business Information Edited",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const addBusinessInfoStatus = useMutation<
    ApiResponse<unknown>,
    ApiError<ValidationErrors>,
    StatusForms
  >({
    mutationFn: (data) => {
      return GenericApi.put<StatusForms>(
        `${ApiRoutes.BUSINESSINFOSTATUS}?user_id=${selectedClient?.id}`,
        {
          ...data,
        },
      );
    },
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      queryClient.invalidateQueries({ queryKey: [BusinessInfoQueryKey] });
      queryClient.invalidateQueries({
        queryKey: [
          CoPQueryKey,
          selectedClient?.id ? selectedClient?.id : localUser?.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [GenericStatusQueryKey],
      });
      showSnackBar(
        "Business Information Status Updated",
        "success",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      showSnackBar(
        `${error?.name}`,
        "error",
        {
          vertical: "top",
          horizontal: "right",
        },
        5000,
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // This is the payload with all the form data
    const payload = {
      company_legal_name: formData.company_legal_name,
      trade_name: formData.trade_name,

      registered_company_street_address:
        formData.registered_company_street_address,
      registered_company_street_address2:
        formData.registered_company_street_address2,
      registered_company_post_code: formData.registered_company_post_code,
      registered_company_city: formData.registered_company_city,
      registered_company_country: formData.registered_company_country,
      registered_company_registration_number:
        formData.registered_company_registration_number,
      registered_company_incorporation_date:
        formData.registered_company_incorporation_date,
      registered_company_incorporation_city_and_country:
        formData.registered_company_incorporation_city_and_country,
      registered_company_tin: formData.registered_company_tin,
      registered_company_registration_authority:
        formData.registered_company_registration_authority,
      registered_company_listed_on_stock_exchange:
        formData.registered_company_listed_on_stock_exchange,
      business_overview_type_of_business:
        formData.business_overview_type_of_business,
      business_overview_contact_person:
        formData.business_overview_contact_person,
      business_overview_actual_company_address:
        formData.business_overview_actual_company_address,
      business_overview_phone: formData.business_overview_phone,
      business_overview_email: formData.business_overview_email,

      ownership_structures: formData.ownership_structures,
      directors: formData.directors,

      business_model: formData.business_model,
      website: formData.website,
      expected_monthly_transaction_volume:
        formData.expected_monthly_transaction_volume,
      expected_currency:
        formData.expected_currency == "" ? formData.expected_currency : "EUR",
      additional_comments: formData.additional_comments,
      legalQuestions: {
        prosecutionNotice: "NO",
        criminalConvictions: "NO",
        ongoingInvestigations: "NO",
        companyConductGreyList: "NO",
        companyConduct: "NO",
      },
      documents: {
        incorporationCertificate: null,
        goodStandingCertificate: null,
        shareholdersCertificate: null,
        directorsCertificate: null,
        registeredOfficeNotice: null,
        managementIdentification: null,
        authorizationResolution: null,
        authorizedPersonIdentification: null,
      },
    };

    // if (incorporation_certificate === null ||
    //   good_standing_certificate == null ||
    //   shareholders_certificate == null ||
    //   directors_certificate == null ||
    //   notice_of_registered_office == null ||
    //   id_of_person_managing_company == null ||
    //   resolution_who_is_authorised_on_behalf == null ||
    //   id_of_who_is_authorised_on_behalf == null
    // ) {
    //   showSnackBar(
    //     "Please upload all required documents",
    //     "error",
    //     {
    //       vertical: "top",
    //       horizontal: "right",
    //     },
    //     5000
    //   );
    //   return;
    // }
    addBusinessInfo.mutate(payload);
  };

  const [selected, setSelected] = useState("");

  useEffect(() => {
    const initialSelection =
      formData?.registered_company_listed_on_stock_exchange ?? "No";
    setSelected(initialSelection);
  }, [formData]);

  // const handleStatusSubmit = (e: React.FormEvent, status: string) => {
  //   e.preventDefault();

  //   const payload = {
  //     status: status,
  //     decline_reason: declineReason,
  //   };

  //   addBusinessInfoStatus.mutate(payload);
  // };

  const [_error, setError] = useState<{
    type: "manual";
    message: string;
  }>({
    type: "manual",
    message: "",
  });

  useEffect(() => {
    if (selectedClient) {
      const statusFromBackend = userBusinessInfo?.data?.status;
      if (statusFromBackend) {
        setFormData((prev: any) => ({
          ...prev,
          status: statusFromBackend,
        }));
      }
    }
  }, [selectedClient, userBusinessInfo]);

  const handleSubmitWithStatus = (
    e: React.FormEvent,
    statusChanged: string,
  ) => {
    e.preventDefault();

    if (selectedClient) {
      const payload = {
        status: statusChanged,
        decline_reason: declineReason,
      };
      setFormData((prev: any) => ({
        ...prev,
        status: { ...prev.status, status: statusChanged },
      }));
      setError({
        type: "manual",
        message: "",
      });
      addBusinessInfoStatus.mutate(payload, {
        onSuccess: () => {
          setFormData((prev: any) => ({
            ...prev,
            status: { ...prev.status, status: statusChanged },
          }));
        },
      });
    }
  };

  return (
    <div className="w-full mx-auto p-8 Container rounded-tl-[0rem] Text">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Registration Information */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <div
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Company registration information
            </div>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-4 w-2/3 ml-14">
            <div className="w-[81.5%]">
              <label
                htmlFor="company_legal_name"
                className="SmallText light-blue medium"
              >
                Legal name of the company
                <span className="!text-red-500 ml-1">*</span>
              </label>
              <input
                id="company_legal_name"
                name="company_legal_name"
                placeholder="Name"
                value={formData.company_legal_name}
                onChange={handleChange}
                required
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>

            <div className="w-[81.5%]">
              <label
                htmlFor="trade_name"
                className="SmallText light-blue medium"
              >
                Trading name(s) used (if different from above)
              </label>
              <input
                id="trade_name"
                name="trade_name"
                placeholder="Name(s)"
                value={formData.trade_name}
                onChange={handleChange}
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>
          </div>
        </div>

        {/* Registered Company Address */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Registered company address
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-4 w-2/3 ml-14">
            <div className="w-[81.5%]">
              <label
                htmlFor="registered_company_street_address"
                className="SmallText light-blue medium"
              >
                Street Address
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="registered_company_street_address"
                name="registered_company_street_address"
                placeholder="Type"
                value={formData.registered_company_street_address}
                onChange={handleChange}
                required
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="registered_company_street_address2"
                  className="SmallText light-blue medium"
                >
                  Street Address 2
                </label>
                <input
                  id="registered_company_street_address2"
                  name="registered_company_street_address2"
                  placeholder="Type"
                  value={formData.registered_company_street_address2}
                  onChange={handleChange}
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>

              <div>
                <label
                  htmlFor="registered_company_post_code"
                  className="SmallText light-blue medium"
                >
                  Post Code
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="registered_company_post_code"
                  name="registered_company_post_code"
                  placeholder="Type"
                  value={formData.registered_company_post_code}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="registered_company_city"
                  className="SmallText light-blue medium"
                >
                  City
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="registered_company_city"
                  name="registered_company_city"
                  placeholder="Type"
                  value={formData.registered_company_city}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>

              <div ref={containerRef}>
                <label
                  htmlFor="registered_company_country"
                  className="SmallText light-blue medium"
                >
                  Country
                  <span className="text-red-500 ml-1">*</span>
                </label>

                <div className="relative">
                  <div
                    tabIndex={0}
                    onFocus={() => {
                      if (!isDisabled) {
                        setCountryDropdownOpen1(true);
                        setHighlightedIndexCountryDropdown1(
                          filteredCountries.length > 0 ? 0 : -1,
                        );
                      }
                    }}
                    role="button"
                    aria-haspopup="listbox"
                    onClick={() =>
                      !isDisabled
                        ? setCountryDropdownOpen1(!countryDropdownOpen1)
                        : setCountryDropdownOpen1(false)
                    }
                    onKeyDown={(e) => {
                      if (isDisabled) return;

                      if (e.key === "Tab") {
                        if (!countryDropdownOpen1) {
                          e.preventDefault();
                          setCountryDropdownOpen1(true);
                          setHighlightedIndexCountryDropdown1(
                            filteredCountries.length > 0 ? 0 : -1,
                          );
                          return;
                        }
                      }

                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (
                          countryDropdownOpen1 &&
                          highlightedIndexCountryDropdown1 >= 0 &&
                          filteredCountries.length > 0
                        ) {
                          handleSelectChange(
                            "registered_company_country",
                            filteredCountries[highlightedIndexCountryDropdown1]
                              .name,
                          );
                          setCountryDropdownOpen1(false);
                          setHighlightedIndexCountryDropdown1(-1);
                          setSearchTerm("");
                        } else {
                          setCountryDropdownOpen1(true);
                          setHighlightedIndexCountryDropdown1(
                            filteredCountries.length > 0 ? 0 : -1,
                          );
                        }
                      }

                      if (e.key === "Escape") {
                        e.preventDefault();
                        setCountryDropdownOpen1(false);
                        setHighlightedIndexCountryDropdown1(-1);
                        setSearchTerm("");
                      }

                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        if (!countryDropdownOpen1) {
                          setCountryDropdownOpen1(true);
                          setHighlightedIndexCountryDropdown1(
                            filteredCountries.length > 0 ? 0 : -1,
                          );
                        } else {
                          setHighlightedIndexCountryDropdown1((prev) =>
                            prev < filteredCountries.length - 1 ? prev + 1 : 0,
                          );
                        }
                      }

                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        if (!countryDropdownOpen1) {
                          setCountryDropdownOpen1(true);
                          setHighlightedIndexCountryDropdown1(
                            filteredCountries.length > 0 ? 0 : -1,
                          );
                        } else {
                          setHighlightedIndexCountryDropdown1((prev) =>
                            prev > 0 ? prev - 1 : filteredCountries.length - 1,
                          );
                        }
                      }
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-[0.45rem] mt-1 rounded-[0.625rem] bg-[#212121] border border-[#2B2B2B] focus:ring-0 cursor-pointer flex items-center justify-between gap-2 SmallText text-white focus:border-[#2B2B2B]"
                        : "px-4 py-[0.45rem] mt-1 rounded-[0.625rem] bg-white border border-[#D4D4D4] focus:ring-0 cursor-pointer flex items-center justify-between gap-2 SmallText focus:outline focus:outline-1 focus:outline-blue-500"
                    }`}
                  >
                    {formData.registered_company_country || "Select Country"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="10"
                      viewBox="0 0 11 10"
                      fill="none"
                    >
                      <path
                        d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                        fill={isBitramp() ? "#fff" : "#737791"}
                      />
                    </svg>
                  </div>

                  <AnimatePresence>
                    {countryDropdownOpen1 && (
                      <motion.div
                        ref={listRefCountryDropdown1}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className={`${
                          isBitramp()
                            ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                            : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto border border-gray-200"
                        }`}
                      >
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setHighlightedIndexCountryDropdown1(0);
                          }}
                          onKeyDown={(e) => {
                            if (
                              ["ArrowDown", "ArrowUp", "Enter"].includes(e.key)
                            ) {
                              e.preventDefault();
                            }

                            if (
                              e.key === "Enter" &&
                              filteredCountries.length > 0
                            ) {
                              handleSelectChange(
                                "registered_company_country",
                                filteredCountries[
                                  highlightedIndexCountryDropdown1 >= 0
                                    ? highlightedIndexCountryDropdown1
                                    : 0
                                ].name,
                              );
                              setCountryDropdownOpen1(false);
                              setHighlightedIndexCountryDropdown1(-1);
                              setSearchTerm("");
                            }
                          }}
                          className="w-full px-4 py-2 border-b border-gray-200 outline-none text-sm bg-inherit !rounded-r-[0rem] !rounded-bl-[0rem] Input"
                        />
                        {filteredCountries.map((country, index) => (
                          <div
                            key={country.code}
                            onClick={() => {
                              handleSelectChange(
                                "registered_company_country",
                                country.name,
                              );
                              setCountryDropdownOpen1(false);
                              setHighlightedIndexCountryDropdown1(-1);
                              setSearchTerm("");
                            }}
                            onMouseEnter={() =>
                              setHighlightedIndexCountryDropdown1(index)
                            }
                            className={`px-4 py-2 cursor-pointer ${
                              isBitramp()
                                ? highlightedIndexCountryDropdown1 === index
                                  ? "bg-[#262626] rounded-[0.625rem]"
                                  : "hover:bg-[#262626]"
                                : highlightedIndexCountryDropdown1 === index
                                  ? "bg-gray-100"
                                  : "hover:bg-gray-100"
                            }`}
                          >
                            {country.name}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            <div className="w-[81.5%]">
              <label
                htmlFor="registered_company_registration_number"
                className="SmallText light-blue medium"
              >
                Registration number
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="registered_company_registration_number"
                name="registered_company_registration_number"
                placeholder="Type"
                value={formData.registered_company_registration_number}
                onChange={handleChange}
                required
                className={`${
                  isBitramp() ? "placeholder-[#464646]" : ""
                } input_design Input `}
                disabled={isDisabled}
              ></input>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="registered_company_incorporation_date"
                  className="SmallText light-blue medium"
                >
                  Incorporation date
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="date"
                  id="registered_company_incorporation_date"
                  name="registered_company_incorporation_date"
                  value={formData.registered_company_incorporation_date}
                  max={today}
                  onChange={handleChange}
                  className="input_design Input"
                  required
                  disabled={isDisabled}
                />
                <style>
                  {`
                     input[type="date"].input_design::-webkit-calendar-picker-indicator {
                     filter: ${isBitramp() ? "invert(1)" : "invert(0)"};
                     cursor: pointer;
                     }
                 `}
                </style>
              </div>

              <div>
                <label
                  htmlFor="registered_company_incorporation_city_and_country"
                  className="SmallText light-blue medium"
                >
                  City & country of incorporation
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="registered_company_incorporation_city_and_country"
                  name="registered_company_incorporation_city_and_country"
                  placeholder="Type"
                  value={
                    formData.registered_company_incorporation_city_and_country
                  }
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="registered_company_tin"
                  className="SmallText light-blue medium"
                >
                  Tax identification number (TIN)
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="registered_company_tin"
                  name="registered_company_tin"
                  placeholder="Type"
                  value={formData.registered_company_tin}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>

              <div>
                <label
                  htmlFor="registered_company_registration_authority"
                  className="SmallText light-blue medium"
                >
                  Registration authority
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="registered_company_registration_authority"
                  name="registered_company_registration_authority"
                  placeholder="Type"
                  value={formData.registered_company_registration_authority}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div>
              <label className="SmallText light-blue medium">
                Is the company listed on a stock exchange?
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.registered_company_listed_on_stock_exchange}
                onValueChange={handleRadioChange}
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {/* <div className="SmallText light-blue medium space-x-2">
                  <RadioGroupItem
                    value="YES"
                    id="yes"
                    className={`${
                      isBitramp()
                        ? "relative w-5 h-5 rounded-full border-2 border-[#464646] bg-transparent appearance-none data-[state=checked]:bg-transparent data-[state=checked]:after:content-[''] data-[state=checked]:after:absolute data-[state=checked]:after:top-1/2 data-[state=checked]:after:left-1/2 data-[state=checked]:after:-translate-x-1/2 data-[state=checked]:after:-translate-y-1/2 data-[state=checked]:after:w-2.5 data-[state=checked]:after:h-2.5 data-[state=checked]:after:rounded-full data-[state=checked]:after:bg-[#8AFEFD]"
                        : ""
                    }`}
                  />
                  <label htmlFor="yes">YES</label>
                </div> */}

                {isBitramp() ? (
                  <div className="flex space-x-6 mt-[0.3rem]">
                    {["Yes", "No"].map((option) => {
                      const id = `custom-radio-${option.toLowerCase()}`;
                      const isActive = selected === option;

                      return (
                        <div
                          key={option}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            id={id}
                            name="custom-radio"
                            value={option}
                            checked={isActive}
                            onChange={() => setSelected(option)}
                            className="hidden"
                          />
                          <label
                            htmlFor={id}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelected(option);
                              }
                            }}
                            className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 ${
                              isActive ? "font-semibold" : ""
                            }`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <circle
                                cx="16"
                                cy="16"
                                r="15.5"
                                stroke="#464646"
                                strokeWidth="1.5"
                              />
                              {isActive && (
                                <circle cx="16" cy="16" r="9" fill="#8afefd" />
                              )}
                            </svg>
                            <span className="SmallText light-blue font-medium">
                              {option}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex space-x-6 mt-[0.3rem]">
                    {["Yes", "No"].map((option) => {
                      const id = `custom-radio-${option.toLowerCase()}`;
                      const isActive = selected === option;

                      return (
                        <div
                          key={option}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="radio"
                            id={id}
                            name="custom-radio"
                            value={option}
                            checked={isActive}
                            onChange={() => setSelected(option)}
                            className="hidden"
                          />
                          <label
                            htmlFor={id}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelected(option);
                              }
                            }}
                            className={`flex items-center space-x-2 cursor-pointer outline-none focus-within:ring-1 focus-within:ring-gray-300 rounded-[0.4rem] px-1 ${
                              isActive ? "font-semibold" : ""
                            }`}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <circle
                                cx="16"
                                cy="16"
                                r="15.5"
                                stroke="#464646"
                                strokeWidth="1.5"
                              />
                              {isActive && (
                                <circle cx="16" cy="16" r="9" fill="#202a43" />
                              )}
                            </svg>
                            <span className="SmallText light-blue font-medium">
                              {option}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* <div className="SmallText light-blue medium space-x-2">
                  <RadioGroupItem
                    value="NO"
                    id="no"
                    className={`${
                      isBitramp()
                        ? "relative w-5 h-5 rounded-full border-2 border-[#464646] bg-transparent appearance-none data-[state=checked]:bg-transparent data-[state=checked]:after:content-[''] data-[state=checked]:after:absolute data-[state=checked]:after:top-1/2 data-[state=checked]:after:left-1/2 data-[state=checked]:after:-translate-x-1/2 data-[state=checked]:after:-translate-y-1/2 data-[state=checked]:after:w-2.5 data-[state=checked]:after:h-2.5 data-[state=checked]:after:rounded-full data-[state=checked]:after:bg-[#8AFEFD]"
                        : ""
                    }`}
                  />
                  <label htmlFor="no">NO</label>
                </div> */}
              </RadioGroup>
            </div>
          </div>
        </div>

        {/* Business Overview */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Business overview
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-4 w-2/3 ml-14">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="business_overview_type_of_business"
                  className="SmallText light-blue medium"
                >
                  Type of business
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="business_overview_type_of_business"
                  name="business_overview_type_of_business"
                  placeholder="Type"
                  value={formData.business_overview_type_of_business}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>

              <div>
                <label
                  htmlFor="business_overview_contact_person"
                  className="SmallText light-blue medium"
                >
                  Contact Person
                </label>
                <input
                  id="business_overview_contact_person"
                  name="business_overview_contact_person"
                  placeholder="Type"
                  value={formData.business_overview_contact_person}
                  onChange={handleChange}
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="w-[81.5%]">
              <label
                htmlFor="business_overview_actual_company_address"
                className="SmallText light-blue medium"
              >
                Actual company address (if different from registered company
                address)
              </label>
              <input
                id="business_overview_actual_company_address"
                name="business_overview_actual_company_address"
                placeholder="Type"
                value={formData.business_overview_actual_company_address}
                onChange={handleChange}
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="business_overview_phone"
                  className="SmallText light-blue medium"
                >
                  Telephone number
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <MuiPhoneNumber
                  defaultCountry="gi"
                  value={formData.business_overview_phone}
                  disabled={isDisabled}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      business_overview_phone:
                        typeof value === "string" ? value : "",
                    })
                  }
                  inputClass="input_design Input w-full"
                  sx={{
                    "& .MuiInputBase-input": {
                      width: "15rem",
                      fontSize: "0.875rem !important",
                      color: isBitramp()
                        ? "#fff !important"
                        : "#000 !important",
                      ":focus": {
                        border: "none !important",
                        boxShadow: "none !important",
                        outline: "none !important",
                        color: isBitramp()
                          ? "#fff !important"
                          : "#000 !important",
                      },
                      ":hover": {
                        color: isBitramp()
                          ? "#fff !important"
                          : "#000 !important",
                      },
                    },
                    "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                      {
                        color: isBitramp()
                          ? "#fff !important"
                          : "#000 !important",
                      },
                    ".MuiInputBase-root": {
                      width: "100%",
                      paddingY: "0.31rem",
                      color: isBitramp()
                        ? "#fff !important"
                        : "#000 !important",
                      "& input": {
                        color: isBitramp()
                          ? "#fff !important"
                          : "#000 !important",
                      },
                      "::before": {
                        borderBottom: "none !important",
                        display: "none !important",
                      },
                      "::after": {
                        borderBottom: "none !important",
                        display: "none !important",
                      },
                      ":hover::before": {
                        borderBottom: "none !important",
                      },
                      ":focus-within::after": {
                        borderBottom: "none !important",
                      },
                      ":hover:not(.Mui-disabled):before": {
                        borderBottom: "none !important",
                      },
                    },
                    "& .MuiInput-root::before": {
                      borderBottom: "none !important",
                      display: "none !important",
                    },
                    "& .MuiInput-root::after": {
                      borderBottom: "none !important",
                      display: "none !important",
                    },
                    "& .MuiInput-root:hover::before": {
                      borderBottom: "none !important",
                    },
                    "& .MuiInput-root.Mui-focused::after": {
                      borderBottom: "none !important",
                    },
                    ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                      borderBottom: "none !important",
                      display: "none !important",
                    },
                    ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                      borderBottom: "none !important",
                      display: "none !important",
                    },
                    ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                      {
                        borderBottom: "none !important",
                      },
                    ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                      {
                        borderBottom: "none !important",
                      },
                    "& .MuiInputAdornment-root": {
                      marginLeft: "0.6rem !important",
                      paddingLeft: "0.4rem !important",
                      width: "2rem !important",
                    },
                    ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                      {
                        fontSize: "0.875rem !important",
                        color: isBitramp()
                          ? "#fff !important"
                          : "#000 !important",
                      },
                    ".MuiPaper-root": {
                      backgroundColor: "#2B2B2B !important",
                      color: "#fff !important",
                    },
                    ".MuiMenuItem-root": {
                      color: "#fff !important",
                      "&:hover": {
                        backgroundColor: "#1a1a1a !important",
                      },
                    },
                    ".Mui-selected": {
                      backgroundColor: "#202a43 !important",
                    },
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="business_overview_email"
                  className="SmallText light-blue medium"
                >
                  E-mail address
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="business_overview_email"
                  name="business_overview_email"
                  type="email"
                  placeholder="Type"
                  value={formData.business_overview_email}
                  onChange={handleChange}
                  required
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Company Ownership Profile & Ultimate Beneficial Owner */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Company ownership profile & ultimate beneficial owner
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          {formData.ownership_structures.map((structure, index) => {
            const filtered = COUNTRIES.filter((c) =>
              c.name
                .toLowerCase()
                .includes((searchTermOwnership[index] || "").toLowerCase()),
            );

            const openDropdown = () =>
              setOwnershipDropdownOpen((prev) => ({ ...prev, [index]: true }));

            const closeDropdown = () => {
              setOwnershipDropdownOpen((prev) => ({ ...prev, [index]: false }));
              setSearchTermOwnership((prev) => ({ ...prev, [index]: "" }));
              setHighlightedIndexOwnership((prev) => ({
                ...prev,
                [index]: -1,
              }));
            };

            return (
              <div
                key={`ownership-${index}`}
                className={`${
                  isBitramp()
                    ? "border-l border-l-[rgba(0,0,0,0.1)] p-8 rounded-lg space-y-4 w-2/3 ml-6"
                    : "border p-8 rounded-lg space-y-4 w-2/3 ml-6"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <h3
                      className={`${
                        isBitramp() ? "text-white" : "text-lg font-medium"
                      }`}
                    >
                      Ownership Structure
                    </h3>
                    <div
                      className={`${
                        isBitramp()
                          ? "ml-2 bg-[#464646] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
                          : "ml-2 bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center text-sm"
                      } `}
                    >
                      {index + 1}
                    </div>
                  </div>
                  {formData.ownership_structures.length > 1 && !isDisabled && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOwnershipStructure(index)}
                      className={`${
                        isBitramp()
                          ? "Delete_button_bitramp"
                          : "Outline_red_button"
                      } `}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>

                <div>
                  <label
                    htmlFor={`ownership-entity-name-${index}`}
                    className="SmallText light-blue medium"
                  >
                    Entity name (if applicable)
                  </label>
                  <input
                    id={`ownership-entity-name-${index}`}
                    placeholder="Type"
                    value={structure.entity_name}
                    onChange={(e) =>
                      handleOwnershipChange(
                        index,
                        "entity_name",
                        e.target.value,
                      )
                    }
                    className={`input_design Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    disabled={isDisabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-tel-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Telephone number
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <MuiPhoneNumber
                      defaultCountry="gi"
                      value={structure.phone}
                      disabled={isDisabled}
                      onChange={(e) =>
                        handleOwnershipChange(index, "phone", e as string)
                      }
                      inputClass="input_design Input w-full"
                      sx={{
                        "& .MuiInputBase-input": {
                          width: "15rem",
                          fontSize: "0.875rem !important",
                          color: isBitramp()
                            ? "#fff !important"
                            : "#000 !important",
                          ":focus": {
                            border: "none !important",
                            boxShadow: "none !important",
                            outline: "none !important",
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                          ":hover": {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        },
                        "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                          {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        ".MuiInputBase-root": {
                          width: "100%",
                          paddingY: "0.31rem",
                          color: isBitramp()
                            ? "#fff !important"
                            : "#000 !important",
                          "& input": {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                          "::before": {
                            borderBottom: "none !important",
                            display: "none !important",
                          },
                          "::after": {
                            borderBottom: "none !important",
                            display: "none !important",
                          },
                          ":hover::before": {
                            borderBottom: "none !important",
                          },
                          ":focus-within::after": {
                            borderBottom: "none !important",
                          },
                          ":hover:not(.Mui-disabled):before": {
                            borderBottom: "none !important",
                          },
                        },
                        "& .MuiInput-root::before": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        "& .MuiInput-root::after": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        "& .MuiInput-root:hover::before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInput-root.Mui-focused::after": {
                          borderBottom: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                          {
                            borderBottom: "none !important",
                          },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                          {
                            borderBottom: "none !important",
                          },
                        "& .MuiInputAdornment-root": {
                          marginLeft: "0.6rem !important",
                          paddingLeft: "0.4rem !important",
                          width: "2rem !important",
                        },
                        ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                          {
                            fontSize: "0.875rem !important",
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        ".MuiPaper-root": {
                          backgroundColor: "#2B2B2B !important",
                          color: "#fff !important",
                        },
                        ".MuiMenuItem-root": {
                          color: "#fff !important",
                          "&:hover": {
                            backgroundColor: "#1a1a1a !important",
                          },
                        },
                        ".Mui-selected": {
                          backgroundColor: "#202a43 !important",
                        },
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`ownership-email-${index}`}
                      className="SmallText light-blue medium"
                    >
                      E-mail address
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`ownership-email-${index}`}
                      placeholder="Type"
                      value={structure.email}
                      onChange={(e) =>
                        handleOwnershipChange(index, "email", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      type="email"
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`ownership-nature-${index}`}
                    className="SmallText light-blue medium"
                  >
                    Nature of control (e.g. Shareholder, Partner of Partnership,
                    Beneficiary of Trust/Foundation)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id={`ownership-nature-${index}`}
                    placeholder="Type"
                    value={structure.nature_of_control}
                    onChange={(e) =>
                      handleOwnershipChange(
                        index,
                        "nature_of_control",
                        e.target.value,
                      )
                    }
                    required
                    className={`input_design Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    disabled={isDisabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-contact-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Contact Person (only if type is entity)
                    </label>
                    <input
                      id={`ownership-contact-${index}`}
                      placeholder="Type"
                      value={structure.contact_person}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "contact_person",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="flex items-center pt-2 pb-2 w-full">
                  <h2
                    className={`w-fit whitespace-nowrap semi-bold ${
                      isBitramp() ? "text-white" : ""
                    }`}
                  >
                    Natural Person Details
                  </h2>
                  {isBitramp() ? (
                    <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
                  ) : (
                    <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-firstname-${index}`}
                      className="SmallText light-blue medium"
                    >
                      First name
                    </label>
                    <input
                      id={`ownership-firstname-${index}`}
                      placeholder="Type"
                      value={structure.first_name}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "first_name",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`ownership-lastname-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Last name
                    </label>
                    <input
                      id={`ownership-lastname-${index}`}
                      placeholder="Type"
                      value={structure.last_name}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "last_name",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-passport-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Passport/ID number
                    </label>
                    <input
                      id={`ownership-passport-${index}`}
                      placeholder="Type"
                      value={structure.id_number}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "id_number",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`ownership-expiration-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Date of expiration
                    </label>
                    <input
                      type="date"
                      id={`ownership-expiration-${index}`}
                      value={structure.id_expiration || ""}
                      min={calculateMaximum}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "id_expiration",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`ownership-street-${index}`}
                    className="SmallText light-blue medium"
                  >
                    Street Address
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id={`ownership-street-${index}`}
                    placeholder="Type"
                    value={structure.street_address}
                    onChange={(e) =>
                      handleOwnershipChange(
                        index,
                        "street_address",
                        e.target.value,
                      )
                    }
                    required
                    className={`input_design Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    disabled={isDisabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-street2-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Street Address 2
                    </label>
                    <input
                      id={`ownership-street2-${index}`}
                      placeholder="Type"
                      value={structure.street_address2}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "street_address2",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`ownership-postcode-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Post Code
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`ownership-postcode-${index}`}
                      placeholder="Type"
                      value={structure.post_code}
                      onChange={(e) =>
                        handleOwnershipChange(
                          index,
                          "post_code",
                          e.target.value,
                        )
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`ownership-city-${index}`}
                      className="SmallText light-blue medium"
                    >
                      City
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`ownership-city-${index}`}
                      placeholder="Type"
                      value={structure.city}
                      onChange={(e) =>
                        handleOwnershipChange(index, "city", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`ownership-country-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Country
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <div className="relative w-full" ref={ownershipDropdownRef}>
                      <div
                        id={`ownership-country-${index}`}
                        onClick={() => toggleDropdown(index)}
                        onKeyDown={(e) => {
                          if (isDisabled) return;

                          if (e.key === "Tab") {
                            if (!ownershipDropdownOpen[index]) {
                              e.preventDefault();
                              openDropdown();
                              setHighlightedIndexOwnership((prev) => ({
                                ...prev,
                                [index]: filtered.length > 0 ? 0 : -1,
                              }));
                              return;
                            }
                          }

                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (
                              ownershipDropdownOpen[index] &&
                              filtered.length > 0 &&
                              highlightedIndexOwnership[index] !== undefined
                            ) {
                              handleSelect(
                                index,
                                filtered[highlightedIndexOwnership[index]].name,
                              );
                              closeDropdown();
                            } else {
                              openDropdown();
                              setHighlightedIndexOwnership((prev) => ({
                                ...prev,
                                [index]: filtered.length > 0 ? 0 : -1,
                              }));
                            }
                          }

                          if (e.key === "Escape") {
                            e.preventDefault();
                            closeDropdown();
                          }

                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            openDropdown();
                            setHighlightedIndexOwnership((prev) => ({
                              ...prev,
                              [index]:
                                prev[index] === undefined ||
                                prev[index] >= filtered.length - 1
                                  ? 0
                                  : prev[index] + 1,
                            }));
                          }

                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            openDropdown();
                            setHighlightedIndexOwnership((prev) => ({
                              ...prev,
                              [index]:
                                prev[index] === undefined || prev[index] <= 0
                                  ? filtered.length - 1
                                  : prev[index] - 1,
                            }));
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        onFocus={() => {
                          if (!isDisabled) {
                            openDropdown();
                            setHighlightedIndexOwnership((prev) => ({
                              ...prev,
                              [index]: filtered.length > 0 ? 0 : -1,
                            }));
                          }
                        }}
                        className={`px-4 py-[0.45rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                          isDisabled
                            ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                            : isBitramp()
                              ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                              : "cursor-pointer bg-white border border-[#D4D4D4] Input"
                        }`}
                      >
                        {structure.country || "Select Country"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                        >
                          <path
                            d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                            fill={isBitramp() ? "#fff" : "#737791"}
                          />
                        </svg>
                      </div>

                      <AnimatePresence>
                        {ownershipDropdownOpen[index] && !isDisabled && (
                          <motion.div
                            ref={(el) => (listRefOwnership.current[index] = el)}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className={`${
                              isBitramp()
                                ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                                : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                            }`}
                          >
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={searchTermOwnership[index] || ""}
                              onChange={(e) => {
                                setSearchTermOwnership((prev) => ({
                                  ...prev,
                                  [index]: e.target.value,
                                }));
                                setHighlightedIndexOwnership((prev) => ({
                                  ...prev,
                                  [index]: 0,
                                }));
                              }}
                              onKeyDown={(e) => {
                                if (
                                  ["ArrowDown", "ArrowUp", "Enter"].includes(
                                    e.key,
                                  )
                                )
                                  e.preventDefault();
                                if (e.key === "Enter" && filtered.length > 0) {
                                  handleSelect(
                                    index,
                                    filtered[
                                      highlightedIndexOwnership[index] ?? 0
                                    ].name,
                                  );
                                  closeDropdown();
                                }
                              }}
                              className="w-full px-4 py-2 border-b border-gray-200 outline-none text-sm bg-inherit Input !rounded-r-[0rem] !rounded-bl-[0rem]"
                            />
                            {filtered.length > 0 ? (
                              filtered.map((country, i) => (
                                <div
                                  key={country.code}
                                  onClick={() => {
                                    handleSelect(index, country.name);
                                    closeDropdown();
                                  }}
                                  onMouseEnter={() =>
                                    setHighlightedIndexOwnership((prev) => ({
                                      ...prev,
                                      [index]: i,
                                    }))
                                  }
                                  className={`px-4 py-2 cursor-pointer ${
                                    isBitramp()
                                      ? highlightedIndexOwnership[index] === i
                                        ? "bg-[#262626] rounded-[0.625rem]"
                                        : "hover:bg-[#262626]"
                                      : highlightedIndexOwnership[index] === i
                                        ? "bg-gray-100"
                                        : "hover:bg-gray-100"
                                  }`}
                                >
                                  {country.name}
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-gray-400 italic">
                                No results
                              </div>
                            )}
                            <div
                              onClick={() => {
                                handleSelect(index, "Other");
                                closeDropdown();
                              }}
                              className={`px-4 py-2 cursor-pointer ${
                                isBitramp()
                                  ? "hover:bg-[#262626]"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              Other
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="SmallText light-blue medium flex items-center">
                    PEP (Politically Exposed Person)
                    <span className="text-red-500 ml-1">*</span>
                    <Tooltip
                      className={`${
                        isBitramp()
                          ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                          : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
                      } `}
                      content="**‘Politically exposed person’ means a natural person who is or who has been entrusted with prominent public functions and includes the following: (a) heads of state, heads of government, ministers and deputy or assistant ministers; (b) members of parliament or of similar legislative bodies; (c) members of the governing bodies of political parties; (d) members of supreme courts, of constitutional courts or of other high-level judicial bodies, the decisions of which are not subject to further appeal, except in exceptional circumstances; (e) members of courts of auditors or of the boards of central banks; (f) ambassadors and high-ranking officers in the armed forces; (g) members of the administrative, management or supervisory bodies of state-owned enterprises; (h) directors, deputy directors and members of the board or equivalent function of an international organisation. No public function referred to in points (a) to (h) shall be understood as covering middle-ranking or more junior officials;You also meet the definition of a PEP if you are a close associate or family member of a PEP. Family members include a PEP’s parents, partner (or equivalent), their children and their partners (or equivalent), while a close associate includes a natural person with whom the PEP shares a common business interest or beneficial ownership of a legal entity, legal arrangement or similar or a natural person who has sole beneficial ownership of a legal entity, legal arrangement or similar that is known to have been set up for the benefit of a PEP."
                      placement="right"
                      trigger="hover"
                    >
                      <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
                        ?
                      </div>
                    </Tooltip>
                  </label>
                  <div className="text-sm text-gray-500 mb-1">
                    (only applies to natural person)
                  </div>
                  <RadioGroup
                    value={String(structure.pep)}
                    onValueChange={(value) =>
                      handleOwnershipChange(index, "pep", value)
                    }
                    className="flex space-x-8 mt-2"
                    disabled={isDisabled}
                  >
                    {isBitramp() ? (
                      <div className="flex space-x-6 mt-[0.3rem]">
                        {["YES", "NO"].map((option) => {
                          const optionValue = option.toUpperCase() === "YES";
                          const id = `ownership-pep-${option.toLowerCase()}-${index}`;
                          const name = `ownership-pep-${index}`;
                          const isActive = structure.pep === optionValue;

                          return (
                            <div
                              key={option}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                id={id}
                                name={name}
                                value={String(optionValue)}
                                checked={isActive}
                                onChange={() =>
                                  handleOwnershipChange(
                                    index,
                                    "pep",
                                    optionValue,
                                  )
                                }
                                className="hidden"
                              />
                              <label
                                htmlFor={id}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOwnershipChange(
                                      index,
                                      "pep",
                                      optionValue,
                                    );
                                  }
                                }}
                                className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 ${
                                  isActive ? "font-semibold" : ""
                                }`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 32 32"
                                  fill="none"
                                >
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="15.5"
                                    stroke="#464646"
                                    strokeWidth="1.5"
                                  />
                                  {isActive && (
                                    <circle
                                      cx="16"
                                      cy="16"
                                      r="9"
                                      fill="#8AFEFD"
                                    />
                                  )}
                                </svg>
                                <span className="SmallText light-blue font-medium">
                                  {option}
                                </span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex space-x-6 mt-[0.3rem]">
                        {["YES", "NO"].map((option) => {
                          const optionValue = option.toUpperCase() === "YES";
                          const id = `ownership-pep-${option.toLowerCase()}-${index}`;
                          const name = `ownership-pep-${index}`;
                          const isActive = structure.pep === optionValue;

                          return (
                            <div
                              key={option}
                              className="flex items-center space-x-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                id={id}
                                name={name}
                                value={String(optionValue)}
                                checked={isActive}
                                onChange={() =>
                                  handleOwnershipChange(
                                    index,
                                    "pep",
                                    optionValue,
                                  )
                                }
                                className="hidden"
                              />
                              <label
                                htmlFor={id}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOwnershipChange(
                                      index,
                                      "pep",
                                      optionValue,
                                    );
                                  }
                                }}
                                className={`flex items-center space-x-2 cursor-pointer outline-none focus-within:ring-1 focus-within:ring-gray-300 rounded-[0.4rem] px-1 ${
                                  isActive ? "font-semibold" : ""
                                }`}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 32 32"
                                  fill="none"
                                >
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="15.5"
                                    stroke="#464646"
                                    strokeWidth="1.5"
                                  />
                                  {isActive && (
                                    <circle
                                      cx="16"
                                      cy="16"
                                      r="9"
                                      fill="#202a43"
                                    />
                                  )}
                                </svg>
                                <span className="SmallText light-blue font-medium">
                                  {option}
                                </span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </RadioGroup>
                </div>
              </div>
            );
          })}

          {!isDisabled && (
            <Button
              type="button"
              variant="outline"
              className={`${
                isBitramp()
                  ? "text-white border-l border-l-[rgba(0,0,0,0.1)] border-t-0 border-r-0 border-b-0 flex items-center ml-6"
                  : "flex items-center ml-6"
              }`}
              onClick={addOwnershipStructure}
            >
              <span className="mr-2">+</span> Add another Ownership Structure
            </Button>
          )}

          {isBitramp() ? (
            <div className="w-full mx-6  h-[1px] bg-[#464646] rounded-lg"></div>
          ) : (
            <div></div>
          )}

          {/* Director */}
          {formData.directors.map((director, index) => {
            const filtered = COUNTRIES.filter((c) =>
              c.name
                .toLowerCase()
                .includes((searchTermDirector[index] || "").toLowerCase()),
            );

            const openDropdown = () =>
              setDirectorDropdownOpen((prev) => ({ ...prev, [index]: true }));

            const closeDropdown = () => {
              setDirectorDropdownOpen((prev) => ({ ...prev, [index]: false }));
              setSearchTermDirector((prev) => ({ ...prev, [index]: "" }));
              setHighlightedIndexDirector((prev) => ({ ...prev, [index]: -1 }));
            };
            return (
              <div
                key={`director-${index}`}
                className={`${
                  isBitramp()
                    ? "border-l border-l-[rgba(0,0,0,0.1)] p-8 rounded-lg space-y-4 w-2/3 ml-6"
                    : "border p-8 rounded-lg space-y-4 w-2/3 ml-6"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <h3
                      className={`${
                        isBitramp() ? "text-white" : "text-lg font-medium"
                      }`}
                    >
                      Director
                    </h3>
                    <div
                      className={`${
                        isBitramp()
                          ? "ml-2 bg-[#464646] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
                          : "ml-2 bg-gray-200 rounded-full w-6 h-6 flex items-center justify-center text-sm"
                      } `}
                    >
                      {index + 1}
                    </div>
                  </div>

                  {formData.directors.length > 1 && !isDisabled && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDirector(index)}
                      className={`${
                        isBitramp()
                          ? "Delete_button_bitramp"
                          : "Outline_red_button"
                      } `}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`director-tel-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Telephone number
                      <span className="text-red-500 ml-1">*</span>
                    </label>

                    <MuiPhoneNumber
                      defaultCountry="gi"
                      value={director.phone}
                      disabled={isDisabled}
                      onChange={(e) =>
                        handleDirectorChange(index, "phone", e as string)
                      }
                      inputClass="input_design Input w-full"
                      sx={{
                        "& .MuiInputBase-input": {
                          width: "15rem",
                          fontSize: "0.875rem !important",
                          color: isBitramp()
                            ? "#fff !important"
                            : "#000 !important",
                          ":focus": {
                            border: "none !important",
                            boxShadow: "none !important",
                            outline: "none !important",
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                          ":hover": {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        },
                        "& .MuiInputBase-input, & .MuiInputBase-input:focus, & .MuiInputBase-input:hover, & .MuiInputBase-input:active":
                          {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        ".MuiInputBase-root": {
                          width: "100%",
                          paddingY: "0.31rem",
                          color: isBitramp()
                            ? "#fff !important"
                            : "#000 !important",
                          "& input": {
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                          "::before": {
                            borderBottom: "none !important",
                            display: "none !important",
                          },
                          "::after": {
                            borderBottom: "none !important",
                            display: "none !important",
                          },
                          ":hover::before": {
                            borderBottom: "none !important",
                          },
                          ":focus-within::after": {
                            borderBottom: "none !important",
                          },
                          ":hover:not(.Mui-disabled):before": {
                            borderBottom: "none !important",
                          },
                        },
                        "& .MuiInput-root::before": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        "& .MuiInput-root::after": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        "& .MuiInput-root:hover::before": {
                          borderBottom: "none !important",
                        },
                        "& .MuiInput-root.Mui-focused::after": {
                          borderBottom: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root::before": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
                          borderBottom: "none !important",
                          display: "none !important",
                        },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled, .Mui-error):before":
                          {
                            borderBottom: "none !important",
                          },
                        ".css-5h82ro-MuiInputBase-root-MuiInput-root.Mui-focused::after":
                          {
                            borderBottom: "none !important",
                          },
                        "& .MuiInputAdornment-root": {
                          marginLeft: "0.6rem !important",
                          paddingLeft: "0.4rem !important",
                          width: "2rem !important",
                        },
                        ".css-1yrc8ca-MuiInputBase-input-MuiInput-input, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:focus, .css-1yrc8ca-MuiInputBase-input-MuiInput-input:hover":
                          {
                            fontSize: "0.875rem !important",
                            color: isBitramp()
                              ? "#fff !important"
                              : "#000 !important",
                          },
                        ".MuiPaper-root": {
                          backgroundColor: "#2B2B2B !important",
                          color: "#fff !important",
                        },
                        ".MuiMenuItem-root": {
                          color: "#fff !important",
                          "&:hover": {
                            backgroundColor: "#1a1a1a !important",
                          },
                        },
                        ".Mui-selected": {
                          backgroundColor: "#202a43 !important",
                        },
                      }}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`director-email-${index}`}
                      className="SmallText light-blue medium"
                    >
                      E-mail address
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-email-${index}`}
                      placeholder="Type"
                      value={director.email}
                      onChange={(e) =>
                        handleDirectorChange(index, "email", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      type="email"
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div>
                  <label className="SmallText light-blue medium flex items-center">
                    PEP (Politically Exposed Person)
                    <span className="text-red-500 ml-1">*</span>
                    <Tooltip
                      className={`${
                        isBitramp()
                          ? "bg-[#464646] text-[#ADADAD] w-[40%] pointer-events-none"
                          : "bg-[#F6F6F6] text-[#ADADAD] w-[40%] pointer-events-none"
                      } `}
                      content="**‘Politically exposed person’ means a natural person who is or who has been entrusted with prominent public functions and includes the following: (a) heads of state, heads of government, ministers and deputy or assistant ministers; (b) members of parliament or of similar legislative bodies; (c) members of the governing bodies of political parties; (d) members of supreme courts, of constitutional courts or of other high-level judicial bodies, the decisions of which are not subject to further appeal, except in exceptional circumstances; (e) members of courts of auditors or of the boards of central banks; (f) ambassadors and high-ranking officers in the armed forces; (g) members of the administrative, management or supervisory bodies of state-owned enterprises; (h) directors, deputy directors and members of the board or equivalent function of an international organisation. No public function referred to in points (a) to (h) shall be understood as covering middle-ranking or more junior officials;You also meet the definition of a PEP if you are a close associate or family member of a PEP. Family members include a PEP’s parents, partner (or equivalent), their children and their partners (or equivalent), while a close associate includes a natural person with whom the PEP shares a common business interest or beneficial ownership of a legal entity, legal arrangement or similar or a natural person who has sole beneficial ownership of a legal entity, legal arrangement or similar that is known to have been set up for the benefit of a PEP."
                      placement="right"
                      trigger="hover"
                    >
                      <div className="ml-4 w-[1.2rem] h-[1.2rem] rounded-[50%] bg-[#737791] flex justify-center items-center text-[#ffffff] font-bold">
                        ?
                      </div>
                    </Tooltip>
                  </label>
                  <RadioGroup
                    value={String(director.pep)}
                    onValueChange={(value) =>
                      handleDirectorChange(index, "pep", value)
                    }
                    className="flex space-x-8 mt-2"
                    disabled={isDisabled}
                  >
                    {/* <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={String(true)}
                      id={`director-pep-yes-${index}`}
                    />
                    <label htmlFor={`director-pep-yes-${index}`}>YES</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value={String(false)}
                      id={`director-pep-no-${index}`}
                    />
                    <label htmlFor={`director-pep-no-${index}`}>NO</label>
                  </div> */}

                    {isBitramp() ? (
                      <div className="flex space-x-6 mt-[0.3rem]">
                        {["YES", "NO"].map((option) => {
                          const optionValue = option.toUpperCase() === "YES";
                          const isActive =
                            String(director.pep) === String(optionValue);

                          return (
                            <div
                              key={option}
                              role="radio"
                              aria-checked={isActive}
                              tabIndex={0}
                              onClick={() =>
                                handleDirectorChange(
                                  index,
                                  "pep",
                                  String(optionValue),
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleDirectorChange(
                                    index,
                                    "pep",
                                    String(optionValue),
                                  );
                                }
                              }}
                              className={`flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 ${
                                isActive ? "font-semibold" : ""
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <circle
                                  cx="16"
                                  cy="16"
                                  r="15.5"
                                  stroke="#464646"
                                  strokeWidth="1.5"
                                />
                                {isActive && (
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="9"
                                    fill="#8AFEFD"
                                  />
                                )}
                              </svg>
                              <span className="SmallText light-blue font-medium">
                                {option}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex space-x-6 mt-[0.3rem]">
                        {["YES", "NO"].map((option) => {
                          const optionValue = option.toUpperCase() === "YES";
                          const isActive =
                            String(director.pep) === String(optionValue);

                          return (
                            <div
                              key={option}
                              role="radio"
                              aria-checked={isActive}
                              tabIndex={0}
                              onClick={() =>
                                handleDirectorChange(
                                  index,
                                  "pep",
                                  String(optionValue),
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleDirectorChange(
                                    index,
                                    "pep",
                                    String(optionValue),
                                  );
                                }
                              }}
                              className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 ${
                                isActive ? "font-semibold" : ""
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 32 32"
                                fill="none"
                              >
                                <circle
                                  cx="16"
                                  cy="16"
                                  r="15.5"
                                  stroke="#464646"
                                  strokeWidth="1.5"
                                />
                                {isActive && (
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="9"
                                    fill="#202a43"
                                  />
                                )}
                              </svg>
                              <span className="SmallText light-blue font-medium">
                                {option}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`director-firstname-${index}`}
                      className="SmallText light-blue medium"
                    >
                      First name
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-firstname-${index}`}
                      placeholder="Type"
                      value={director.first_name}
                      onChange={(e) =>
                        handleDirectorChange(
                          index,
                          "first_name",
                          e.target.value,
                        )
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`director-lastname-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Last name
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-lastname-${index}`}
                      placeholder="Type"
                      value={director.last_name}
                      onChange={(e) =>
                        handleDirectorChange(index, "last_name", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`director-passport-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Passport/ID number
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-passport-${index}`}
                      placeholder="Type"
                      value={director.id_number}
                      onChange={(e) =>
                        handleDirectorChange(index, "id_number", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`director-expiration-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Date of expiration
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="date"
                      id={`director-expiration-${index}`}
                      value={director.id_expiration}
                      min={calculateMaximum}
                      onChange={(e) =>
                        handleDirectorChange(
                          index,
                          "id_expiration",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      required
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`director-street-${index}`}
                    className="SmallText light-blue medium"
                  >
                    Street Address
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    id={`director-street-${index}`}
                    placeholder="Type"
                    value={director.street_address}
                    onChange={(e) =>
                      handleDirectorChange(
                        index,
                        "street_address",
                        e.target.value,
                      )
                    }
                    required
                    className={`input_design Input ${
                      isBitramp() ? "placeholder:text-[#464646]" : ""
                    } `}
                    disabled={isDisabled}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`director-street2-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Street Address 2
                    </label>
                    <input
                      id={`director-street2-${index}`}
                      placeholder="Type"
                      value={director.street_address2}
                      onChange={(e) =>
                        handleDirectorChange(
                          index,
                          "street_address2",
                          e.target.value,
                        )
                      }
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`director-postcode-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Post Code
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-postcode-${index}`}
                      placeholder="Type"
                      value={director.post_code}
                      onChange={(e) =>
                        handleDirectorChange(index, "post_code", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor={`director-city-${index}`}
                      className="SmallText light-blue medium"
                    >
                      City
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      id={`director-city-${index}`}
                      placeholder="Type"
                      value={director.city}
                      onChange={(e) =>
                        handleDirectorChange(index, "city", e.target.value)
                      }
                      required
                      className={`input_design Input ${
                        isBitramp() ? "placeholder:text-[#464646]" : ""
                      } `}
                      disabled={isDisabled}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`director-country-${index}`}
                      className="SmallText light-blue medium"
                    >
                      Country
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative w-full" ref={directorDropdownRef}>
                      <div
                        id={`director-country-${index}`}
                        onClick={() => toggleDirectorDropdown(index)}
                        tabIndex={0}
                        role="button"
                        onFocus={() => {
                          if (!isDisabled) {
                            openDropdown();
                            setHighlightedIndexDirector((prev) => ({
                              ...prev,
                              [index]: filtered.length > 0 ? 0 : -1,
                            }));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (isDisabled) return;

                          if (e.key === "Tab") {
                            if (!directorDropdownOpen[index]) {
                              e.preventDefault();
                              openDropdown();
                              setHighlightedIndexDirector((prev) => ({
                                ...prev,
                                [index]: filtered.length > 0 ? 0 : -1,
                              }));
                              return;
                            }
                          }

                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (
                              directorDropdownOpen[index] &&
                              filtered.length > 0 &&
                              highlightedIndexDirector[index] !== undefined
                            ) {
                              handleDirectorSelect(
                                index,
                                filtered[highlightedIndexDirector[index]].name,
                              );
                              closeDropdown();
                            } else {
                              openDropdown();
                              setHighlightedIndexDirector((prev) => ({
                                ...prev,
                                [index]: filtered.length > 0 ? 0 : -1,
                              }));
                            }
                          }

                          if (e.key === "Escape") {
                            e.preventDefault();
                            closeDropdown();
                          }

                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            openDropdown();
                            setHighlightedIndexDirector((prev) => ({
                              ...prev,
                              [index]:
                                prev[index] === undefined ||
                                prev[index] >= filtered.length - 1
                                  ? 0
                                  : prev[index] + 1,
                            }));
                          }

                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            openDropdown();
                            setHighlightedIndexDirector((prev) => ({
                              ...prev,
                              [index]:
                                prev[index] === undefined || prev[index] <= 0
                                  ? filtered.length - 1
                                  : prev[index] - 1,
                            }));
                          }
                        }}
                        className={`px-4 py-[0.45rem] mt-1 rounded-[0.625rem] focus:ring-0 flex items-center justify-between gap-2 SmallText ${
                          isDisabled
                            ? "cursor-not-allowed bg-gray-100 text-gray-400 border border-[#d1d5db]"
                            : isBitramp()
                              ? "cursor-pointer bg-[#212121] border border-[#2B2B2B] text-white"
                              : "cursor-pointer bg-white border border-[#D4D4D4] Input"
                        }`}
                      >
                        {director.country || "Select Country"}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="10"
                          viewBox="0 0 11 10"
                          fill="none"
                        >
                          <path
                            d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                            fill={isBitramp() ? "#fff" : "#737791"}
                          />
                        </svg>
                      </div>
                      <AnimatePresence>
                        {directorDropdownOpen[index] && !isDisabled && (
                          <motion.div
                            ref={(el) => (listRefDirector.current[index] = el)}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className={`${
                              isBitramp()
                                ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                                : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                            }`}
                          >
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={searchTermDirector[index] || ""}
                              onChange={(e) => {
                                setSearchTermDirector((prev) => ({
                                  ...prev,
                                  [index]: e.target.value,
                                }));
                                setHighlightedIndexDirector((prev) => ({
                                  ...prev,
                                  [index]: 0,
                                }));
                              }}
                              onKeyDown={(e) => {
                                if (
                                  ["ArrowDown", "ArrowUp", "Enter"].includes(
                                    e.key,
                                  )
                                )
                                  e.preventDefault();
                                if (e.key === "Enter" && filtered.length > 0) {
                                  handleDirectorSelect(
                                    index,
                                    filtered[
                                      highlightedIndexDirector[index] ?? 0
                                    ].name,
                                  );
                                  closeDropdown();
                                }
                              }}
                              className="w-full px-4 py-2 border-b border-gray-200 outline-none text-sm bg-inherit Input !rounded-r-[0rem] !rounded-bl-[0rem]"
                            />
                            {filtered.length > 0 ? (
                              filtered.map((country, i) => (
                                <div
                                  key={country.code}
                                  onClick={() => {
                                    handleDirectorSelect(index, country.name);
                                    closeDropdown();
                                  }}
                                  onMouseEnter={() =>
                                    setHighlightedIndexDirector((prev) => ({
                                      ...prev,
                                      [index]: i,
                                    }))
                                  }
                                  className={`px-4 py-2 cursor-pointer ${
                                    isBitramp()
                                      ? highlightedIndexDirector[index] === i
                                        ? "bg-[#262626]"
                                        : "hover:bg-[#262626]"
                                      : highlightedIndexDirector[index] === i
                                        ? "bg-gray-100"
                                        : "hover:bg-gray-100"
                                  }`}
                                >
                                  {country.name}
                                </div>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-gray-400 italic">
                                No results
                              </div>
                            )}

                            <div
                              onClick={() => {
                                handleDirectorSelect(index, "Other");
                                closeDropdown();
                              }}
                              className={`px-4 py-2 cursor-pointer ${
                                isBitramp()
                                  ? "hover:bg-[#262626]"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              Other
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {!isDisabled && (
            <Button
              type="button"
              variant="outline"
              className={`${
                isBitramp()
                  ? "text-white border-l border-l-[rgba(0,0,0,0.1)] border-t-0 border-r-0 border-b-0 flex items-center ml-6"
                  : "flex items-center ml-6"
              }`}
              onClick={addDirector}
            >
              <span className="mr-2">+</span> Add another Director
            </Button>
          )}
        </div>

        {/* Primary Business Activities Of The Company */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Primary business activities of the company
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-4 w-2/3 ml-14">
            <div className="w-[81.5%]">
              <label
                htmlFor="business_model"
                className="SmallText light-blue medium"
              >
                Please describe details of products/services (explain business
                model)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="business_model"
                name="business_model"
                placeholder="Type"
                value={formData.business_model}
                onChange={handleBusinessActivitiesChange}
                required
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-[81.5%]">
              <div>
                <label
                  htmlFor="website"
                  className="SmallText light-blue medium"
                >
                  Website
                </label>
                <input
                  id="website"
                  name="website"
                  placeholder="Type"
                  value={formData.website}
                  onChange={handleBusinessActivitiesChange}
                  className={`input_design Input ${
                    isBitramp() ? "placeholder:text-[#464646]" : ""
                  } `}
                  disabled={isDisabled}
                />
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="expected_monthly_transaction_volume"
                  className="SmallText light-blue medium"
                >
                  Expected monthly transactions volume (in EUR)
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="number"
                  id="expected_monthly_transaction_volume"
                  name="expected_monthly_transaction_volume"
                  placeholder="Type"
                  value={formData.expected_monthly_transaction_volume}
                  onChange={handleBusinessActivitiesChange}
                  required
                  className="input_design  Input !p-[0.44rem]"
                  disabled={isDisabled}
                  min={0}
                />
              </div>
            </div>

            {/* <div className="w-[81.5%]">
              <label htmlFor="expected_currency" className="SmallText light-blue medium">
                Expected currency (EUR/USD/USDT/Other)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="expected_currency"
                name="expected_currency"
                placeholder="Type"
                value={formData.expected_currency}
                onChange={handleBusinessActivitiesChange}
                required
                className={`input_design Input ${isBitramp() ? "placeholder:text-[#464646]" : ""} `}
                disabled={isDisabled}
              />
            </div> */}
          </div>
        </div>

        {/* Other */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Other
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-4 w-2/3 ml-14">
            <div className="w-[81.5%]">
              <label className="SmallText light-blue medium">
                Have you or any of the directors, shareholders or ultimate
                beneficial owners received a notice of prosecution, or have any
                prosecutions pending? (Yes/No)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.legalQuestions.prosecutionNotice}
                onValueChange={(value) =>
                  handleLegalQuestionChange("prosecutionNotice", value)
                }
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {/* <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="YES" id="prosecution-yes" />
                  <label htmlFor="prosecution-yes">YES</label>
                </div>
                <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="NO" id="prosecution-no" />
                  <label htmlFor="prosecution-no">NO</label>
                </div> */}

                {isBitramp() ? (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Prosecution Notice"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.prosecutionNotice === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "prosecutionNotice",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "prosecutionNotice",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#8AFEFD" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Prosecution Notice"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.prosecutionNotice === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "prosecutionNotice",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "prosecutionNotice",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#202a43" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RadioGroup>
            </div>

            <div className="w-[81.5%]">
              <label className="SmallText light-blue medium">
                Have you or any of the directors, shareholders or ultimate
                beneficial owners had any criminal convictions within the last
                five years? (Yes/No)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.legalQuestions.criminalConvictions}
                onValueChange={(value) =>
                  handleLegalQuestionChange("criminalConvictions", value)
                }
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {isBitramp() ? (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Criminal Convictions"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.criminalConvictions === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "criminalConvictions",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "criminalConvictions",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#8AFEFD" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Criminal Convictions"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.criminalConvictions === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "criminalConvictions",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "criminalConvictions",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#202a43" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RadioGroup>
            </div>

            <div className="w-[81.5%]">
              <label className="SmallText light-blue medium">
                Are there any ongoing investigations or inquiries, either by or
                on behalf of any government or other body in respect of the
                affairs of your company and any of its subsidiaries or parent
                companies? (Yes/No)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.legalQuestions.ongoingInvestigations}
                onValueChange={(value) =>
                  handleLegalQuestionChange("ongoingInvestigations", value)
                }
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {/* <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="YES" id="investigations-yes" />
                  <label htmlFor="investigations-yes">YES</label>
                </div>
                <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="NO" id="investigations-no" />
                  <label htmlFor="investigations-no">NO</label>
                </div> */}

                {isBitramp() ? (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Ongoing Investigations"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.ongoingInvestigations === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "ongoingInvestigations",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "ongoingInvestigations",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#8AFEFD" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Ongoing Investigations"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.ongoingInvestigations === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "ongoingInvestigations",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "ongoingInvestigations",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#202a43" />
                            )}
                          </svg>
                          <span className="font-medium light-blue">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RadioGroup>
            </div>

            <div className="w-[81.5%]">
              <label className="SmallText light-blue medium">
                DOES THE COMPANY CONDUCT ANY BUSINESS ACTIVITY (DIRECTLY OR
                INDIRECTLY) WITH ENTITIES OR INDIVIDUALS FROM ANY OF THE
                FOLLOWING JURISDICTIONS : Afghanistan, Belarus, Burma (Myanmar),
                Central African Republic, Crimea/
                Donetsk/Kherson/Luhansk/Zaporizhzhia regions of Ukraine, Cuba,
                Ethiopia, Iran, Iraq, Lebanon, Libya, Mali, Nicaragua, North
                Korea, Russia, Somalia, South Sudan, South Ossetia region of
                Georgia, Sudan, Syria, Venezuela, Yemen? (Yes/No)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.legalQuestions.companyConduct}
                onValueChange={(value) =>
                  handleLegalQuestionChange("companyConduct", value)
                }
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {/* <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="YES" id="convictions-yes" />
                  <label htmlFor="convictions-yes">YES</label>
                </div>
                <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="NO" id="convictions-no" />
                  <label htmlFor="convictions-no">NO</label>
                </div> */}

                {isBitramp() ? (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Company Conduct"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.companyConduct === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange("companyConduct", value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "companyConduct",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#8AFEFD" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Company Conduct"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.companyConduct === value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange("companyConduct", value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "companyConduct",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#202a43" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RadioGroup>
            </div>

            <div className="w-[81.5%]">
              <label className="SmallText light-blue medium">
                DOES THE COMPANY CONDUCT ANY BUSINESS ACTIVITY (DIRECTLY OR
                INDIRECTLY) WITH ENTITIES OR INDIVIDUALS FROM ANY OF THE
                FOLLOWING JURISDICTIONS (THE FATF “GREY LIST”): Algeria, Angola,
                Bulgaria, Burkina Faso, Cameroon, Côte d'Ivoire, Croatia,
                Democratic Republic of Congo, Haiti, Kenya, Lao People's
                Democratic Republic, Monaco, Mozambique, Namibia, Nepal,
                Nigeria, South Africa, Tanzania, Vietnam? (Yes/No)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <RadioGroup
                value={formData.legalQuestions.companyConductGreyList}
                onValueChange={(value) =>
                  handleLegalQuestionChange("companyConductGreyList", value)
                }
                className="flex space-x-8 mt-2"
                disabled={isDisabled}
              >
                {/* <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="YES" id="convictions-yes" />
                  <label htmlFor="convictions-yes">YES</label>
                </div>
                <div className="flex items-center space-x-2 SmallText">
                  <RadioGroupItem value="NO" id="convictions-no" />
                  <label htmlFor="convictions-no">NO</label>
                </div> */}

                {isBitramp() ? (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Company Conduct Grey List"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.companyConductGreyList ===
                        value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "companyConductGreyList",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "companyConductGreyList",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#8AFEFD" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    className="flex space-x-6 mt-[0.3rem]"
                    role="radiogroup"
                    aria-label="Company Conduct Grey List"
                  >
                    {["YES", "NO"].map((value) => {
                      const isChecked =
                        formData.legalQuestions.companyConductGreyList ===
                        value;

                      return (
                        <div
                          key={value}
                          role="radio"
                          aria-checked={isChecked}
                          tabIndex={0}
                          onClick={() =>
                            handleLegalQuestionChange(
                              "companyConductGreyList",
                              value,
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleLegalQuestionChange(
                                "companyConductGreyList",
                                value,
                              );
                            }
                          }}
                          className={`flex items-center space-x-2 cursor-pointer outline-none rounded-[0.4rem] px-1 SmallText ${
                            isChecked ? "font-semibold" : ""
                          }`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 32 32"
                            fill="none"
                          >
                            <circle
                              cx="16"
                              cy="16"
                              r="15.5"
                              stroke="#464646"
                              strokeWidth="1.5"
                            />
                            {isChecked && (
                              <circle cx="16" cy="16" r="9" fill="#202a43" />
                            )}
                          </svg>
                          <span className="light-blue font-medium">
                            {value}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </RadioGroup>
            </div>

            <div className="w-[81.5%]">
              <label
                htmlFor="additional_comments"
                className="SmallText light-blue medium"
              >
                Additional comments
              </label>
              <textarea
                id="additional_comments"
                name="additional_comments"
                placeholder="Type"
                value={formData.additional_comments}
                onChange={handleChange}
                className={`input_design Input ${
                  isBitramp() ? "placeholder:text-[#464646]" : ""
                } `}
                disabled={isDisabled}
              />
            </div>
          </div>
        </div>

        {/* Compliance, Anti-Money Laundering And Counter Terrorist Financing Requirements */}
        <div className="space-y-6">
          <div className="flex items-center pt-2 pb-2 w-full">
            <h2
              className={`w-fit whitespace-nowrap semi-bold ${
                isBitramp() ? "text-white" : ""
              }`}
            >
              Compliance, Anti-money laundering and counter terrorist financing
              requirements
            </h2>
            {isBitramp() ? (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
            ) : (
              <div className="w-full ms-4 ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
            )}
          </div>

          <div className="space-y-2 w-full pl-14">
            <div className="flex items-center">
              <h3 className="dark-blue bold">
                Please upload a copy of the following documents:
              </h3>
            </div>

            <div className="w-full">
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Certificate of incorporation
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.incorporation_certificate ?? "",
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (incorporation_certificate ??
                      userBusinessInfo?.data?.incorporation_certificate) ||
                      null,
                  )}
                </span>
                {(incorporation_certificate ||
                  userBusinessInfo?.data?.incorporation_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={`${
                  //   userBusinessInfo?.data?.incorporation_certificate ||
                  //   incorporation_certificate
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.incorporation_certificate ||
                        incorporation_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.incorporation_certificate ||
                          incorporation_certificate
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_incorporation"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_incorporation");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Certificate of good standing dated in the last month
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.good_standing_certificate ?? "",
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (good_standing_certificate ??
                      userBusinessInfo?.data?.good_standing_certificate) ||
                      null,
                  )}
                </span>
                {(good_standing_certificate ||
                  userBusinessInfo?.data?.good_standing_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.good_standing_certificate ||
                  //   good_standing_certificate
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.good_standing_certificate ||
                        good_standing_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.good_standing_certificate ||
                          good_standing_certificate
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_good_standing"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_good_standing");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Certificate of shareholders (or any other certificate showing
                  shareholders of entity) dated in the last month
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.shareholders_certificate ?? "",
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (shareholders_certificate ??
                      userBusinessInfo?.data?.shareholders_certificate) ||
                      null,
                  )}
                </span>
                {(shareholders_certificate ||
                  userBusinessInfo?.data?.shareholders_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.shareholders_certificate ||
                  //   shareholders_certificate
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.shareholders_certificate ||
                        shareholders_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.shareholders_certificate ||
                          shareholders_certificate
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_shareholders"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_shareholders");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Certificate of directors (or any other certificate showing
                  representatives of entity) dated in the last month
                </label>
                <span
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.directors_certificate ?? "",
                    )
                  }
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                >
                  {getFileName(
                    (directors_certificate ??
                      userBusinessInfo?.data?.directors_certificate) ||
                      null,
                  )}
                </span>
                {(directors_certificate ||
                  userBusinessInfo?.data?.directors_certificate) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.directors_certificate ||
                  //   directors_certificate
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.directors_certificate ||
                        directors_certificate
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.directors_certificate ||
                          directors_certificate
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="certificate_of_directors"
                    onChange={(e) => {
                      handleFileUpload(e, "certificate_of_directors");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Proof of registered office address
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.notice_of_registered_office ?? "",
                    )
                  }
                >
                  {getFileName(
                    (notice_of_registered_office ??
                      userBusinessInfo?.data?.notice_of_registered_office) ||
                      null,
                  )}
                </span>
                {(notice_of_registered_office ||
                  userBusinessInfo?.data?.notice_of_registered_office) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.notice_of_registered_office ||
                  //   notice_of_registered_office
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.notice_of_registered_office ||
                        notice_of_registered_office
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.notice_of_registered_office ||
                          notice_of_registered_office
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="notice_of_registered_office"
                    onChange={(e) => {
                      handleFileUpload(e, "notice_of_registered_office");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Identification documents of the person(s) managing the company
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data?.id_of_person_managing_company ??
                        "",
                    )
                  }
                >
                  {getFileName(
                    (id_of_person_managing_company ??
                      userBusinessInfo?.data?.id_of_person_managing_company) ||
                      null,
                  )}
                </span>
                {(id_of_person_managing_company ||
                  userBusinessInfo?.data?.id_of_person_managing_company) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.id_of_person_managing_company ||
                  //   id_of_person_managing_company
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data?.id_of_person_managing_company ||
                        id_of_person_managing_company
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data?.id_of_person_managing_company ||
                          id_of_person_managing_company
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="identification_documents"
                    onChange={(e) => {
                      handleFileUpload(e, "identification_documents");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Resolution specifying who is authorised to act on behalf of
                  the company
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data
                        ?.resolution_who_is_authorised_on_behalf ?? "",
                    )
                  }
                >
                  {getFileName(
                    (resolution_who_is_authorised_on_behalf ??
                      userBusinessInfo?.data
                        ?.resolution_who_is_authorised_on_behalf) ||
                      null,
                  )}
                </span>
                {(resolution_who_is_authorised_on_behalf ||
                  userBusinessInfo?.data
                    ?.resolution_who_is_authorised_on_behalf) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data
                  //     ?.resolution_who_is_authorised_on_behalf ||
                  //   resolution_who_is_authorised_on_behalf
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data
                          ?.resolution_who_is_authorised_on_behalf ||
                        resolution_who_is_authorised_on_behalf
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data
                            ?.resolution_who_is_authorised_on_behalf ||
                          resolution_who_is_authorised_on_behalf
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="resolution_specifying"
                    onChange={(e) => {
                      handleFileUpload(e, "resolution_specifying");
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="py-4 flex justify-between items-center w-full">
                <label className="block medium light-blue">
                  Identification document(s) of the person(s) authorised to act
                  on behalf of the company*
                </label>
                <span
                  className="flex-1 text-end cursor-pointer mr-4 dark-gray"
                  onClick={() =>
                    handleOpenDocument(
                      userBusinessInfo?.data
                        ?.id_of_who_is_authorised_on_behalf ?? "",
                    )
                  }
                >
                  {getFileName(
                    (id_of_who_is_authorised_on_behalf ??
                      userBusinessInfo?.data
                        ?.id_of_who_is_authorised_on_behalf) ||
                      null,
                  )}
                </span>
                {(id_of_who_is_authorised_on_behalf ||
                  userBusinessInfo?.data
                    ?.id_of_who_is_authorised_on_behalf) && (
                  <div className="mr-4"></div>
                )}
                <label
                  // className={` ${
                  //   userBusinessInfo?.data?.id_of_who_is_authorised_on_behalf ||
                  //   id_of_who_is_authorised_on_behalf
                  //     ? "Outline_button"
                  //     : "Outline_button"
                  // }`}

                  className={
                    isBitramp()
                      ? userBusinessInfo?.data
                          ?.id_of_who_is_authorised_on_behalf ||
                        id_of_who_is_authorised_on_behalf
                        ? "Upload_button_bitramp"
                        : "Upload_button_bitramp"
                      : userBusinessInfo?.data
                            ?.id_of_who_is_authorised_on_behalf ||
                          id_of_who_is_authorised_on_behalf
                        ? "Outline_button"
                        : "Outline_button"
                  }
                  tabIndex={0}
                  role="button"
                >
                  Upload
                  <input
                    type="file"
                    name="identification_documents_authorised"
                    onChange={(e) => {
                      handleFileUpload(
                        e,
                        "identification_documents_authorised",
                      );
                    }}
                    disabled={isDisabled}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
        {userBusinessInfo?.data?.status?.status == "declined" ||
        user?.is_staff ? (
          <div className="w-[81.5%] ml-14">
            <label
              htmlFor="decline_reason"
              className="SmallText light-blue medium"
            >
              Decline reason
            </label>
            <textarea
              id="decline_reason"
              name="decline_reason"
              placeholder="Type"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className={`input_design Input ${
                isBitramp() ? "placeholder:text-[#464646]" : ""
              } `}
              disabled={!canEditBusinessInfo}
            />
          </div>
        ) : null}

        {!canEditBusinessInfo && (
          <Button type="submit" className="Primary_button">
            Save
          </Button>
        )}
        <div className="flex gap-4">
          {canEditBusinessInfo && (
            <>
              <button
                type="button"
                disabled={isDisabled}
                onClick={(e) => handleSubmitWithStatus(e, Status.APPROVED)}
                className="Approve_button"
              >
                {formData?.status?.status === Status.APPROVED
                  ? "Approved"
                  : "Approve"}
                {formData?.status?.status === Status.APPROVED ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : null}
              </button>
              <button
                type="button"
                disabled={isDisabled}
                onClick={(e) => handleSubmitWithStatus(e, Status.REJECTED)}
                className={`${
                  isBitramp() ? "Decline_button" : "Outline_red_button"
                } `}
              >
                {formData?.status?.status === Status.REJECTED
                  ? "Declined"
                  : "Decline"}
                {formData?.status?.status === Status.REJECTED ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 ml-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : null}
              </button>
            </>
          )}
          {canEdit ? (
            <Button
              onClick={() => {
                const payload = {
                  company_legal_name: formData.company_legal_name,
                  trade_name: formData.trade_name,

                  registered_company_street_address:
                    formData.registered_company_street_address,
                  registered_company_street_address2:
                    formData.registered_company_street_address2,
                  registered_company_post_code:
                    formData.registered_company_post_code,
                  registered_company_city: formData.registered_company_city,
                  registered_company_country:
                    formData.registered_company_country,
                  registered_company_registration_number:
                    formData.registered_company_registration_number,
                  registered_company_incorporation_date:
                    formData.registered_company_incorporation_date,
                  registered_company_incorporation_city_and_country:
                    formData.registered_company_incorporation_city_and_country,
                  registered_company_tin: formData.registered_company_tin,
                  registered_company_registration_authority:
                    formData.registered_company_registration_authority,
                  registered_company_listed_on_stock_exchange:
                    formData.registered_company_listed_on_stock_exchange,
                  business_overview_type_of_business:
                    formData.business_overview_type_of_business,
                  business_overview_contact_person:
                    formData.business_overview_contact_person,
                  business_overview_actual_company_address:
                    formData.business_overview_actual_company_address,
                  business_overview_phone: formData.business_overview_phone,
                  business_overview_email: formData.business_overview_email,

                  ownership_structures: formData.ownership_structures,
                  directors: formData.directors,

                  business_model: formData.business_model,
                  website: formData.website,
                  expected_monthly_transaction_volume:
                    formData.expected_monthly_transaction_volume,
                  expected_currency:
                    formData.expected_currency == ""
                      ? formData.expected_currency
                      : "EUR",
                  additional_comments: formData.additional_comments,
                  legalQuestions: {
                    prosecutionNotice: "NO",
                    criminalConvictions: "NO",
                    ongoingInvestigations: "NO",
                    companyConductGreyList: "NO",
                    companyConduct: "NO",
                  },
                  documents: {
                    incorporationCertificate: null,
                    goodStandingCertificate: null,
                    shareholdersCertificate: null,
                    directorsCertificate: null,
                    registeredOfficeNotice: null,
                    managementIdentification: null,
                    authorizationResolution: null,
                    authorizedPersonIdentification: null,
                  },
                };
                handleEditBussinesInfo.mutate(payload);
              }}
              className="Primary_button"
            >
              Save
            </Button>
          ) : null}
        </div>
      </form>
    </div>
  );
};

export default BusinessInfoFormNEW;
